import React, { memo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Drawer } from 'antd';
import styled, { css } from 'styled-components';

import { CareerIcon, CareerIconName } from 'atoms';
import { CareerCloseButton } from 'molecules/CareerCloseButton/CareerCloseButton';
import { PATHS } from 'router/config';
import { ACCENT_ICON } from 'services/careerAccents';
import { careerColor, careerTokenVars } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';
import { mobile } from 'services/ui';

/**
 * Боковое меню карьерного раздела (доработка 0071, 15.09).
 *
 * Владелец на стенде: профессии, достижения и обучение открывались только из
 * плана, «непонятно, как туда попасть и как уйти назад», а в обучение нужно
 * попадать одной кнопкой. Четыре верхние вкладки из ТЗ 0071 прятали ровно эти
 * экраны. Решение владельца — боковое меню со ВСЕМИ экранами раздела: знак,
 * подпись, счётчик, цвет раздела.
 *
 * Вложенные пункты («Mapa / Zawody», «Oferty / Aplikacje») видны всегда, без
 * аккордеона: спрятанный пункт — та самая жалоба, которую меню чинит.
 *
 * Подсветка — по ПРЕФИКСАМ адреса: урок подсвечивает «Nauka», страница
 * профессии — «Zawody».
 *
 * «Визуальное упрощение» (15.09): меню больше не спорит с содержимым — знаки
 * одноцветные, без дисков; активный пункт — мягкая жёлтая плашка и вес 500;
 * счётчик — вторичный текст. На телефоне меню уходит в drawer за кнопкой
 * «Menu kariery», на планшете и desktop — колонка постоянной ширины.
 */
export interface ICareerNavBadges {
  /** «5/7» у обучения. */
  lessons?: string;
  /** Активные отклики. */
  applications?: number;
  /** Разделы, закрытые не-участнику: 'lessons' | 'vacancies' | 'applications'. */
  locked?: string[];
}

interface IItem {
  key: string;
  to: string;
  /** Адреса, на которых пункт активен. `exact` — только сам адрес. */
  match: { path: string; exact?: boolean }[];
  icon: CareerIconName;
  /** Возможность из `myCareerJourney.locked`, которая закрывает пункт. */
  capability?: string;
}

interface IGroup {
  key: string;
  items: IItem[];
}

export const NAV_GROUPS: IGroup[] = [
  {
    key: 'plan',
    items: [
      {
        key: 'plan',
        to: PATHS.careerCenter,
        match: [{ path: PATHS.careerCenter, exact: true }],
        icon: ACCENT_ICON.plan,
      },
    ],
  },
  {
    key: 'learning',
    items: [
      {
        key: 'learning',
        to: PATHS.careerLearning,
        match: [{ path: PATHS.careerLearning }],
        icon: ACCENT_ICON.learning,
        capability: 'lessons',
      },
    ],
  },
  {
    key: 'skills',
    items: [
      {
        key: 'skillsMap',
        to: PATHS.careerSkills,
        match: [{ path: PATHS.careerSkills }],
        icon: ACCENT_ICON.skills,
      },
      {
        key: 'professions',
        to: PATHS.careerProfessions,
        match: [{ path: PATHS.careerProfessions }],
        icon: 'briefcase',
      },
    ],
  },
  {
    key: 'work',
    items: [
      {
        key: 'jobs',
        to: PATHS.careerJobs,
        match: [{ path: PATHS.careerJobs }],
        icon: 'search',
        capability: 'vacancies',
      },
      {
        key: 'applications',
        to: PATHS.careerApplications,
        match: [{ path: PATHS.careerApplications }],
        icon: 'applications',
        capability: 'applications',
      },
    ],
  },
  {
    key: 'profile',
    items: [
      {
        key: 'profile',
        to: PATHS.careerProfile,
        match: [{ path: PATHS.careerProfile }],
        icon: ACCENT_ICON.profile,
      },
    ],
  },
  {
    key: 'achievements',
    items: [
      {
        key: 'achievements',
        to: PATHS.careerAchievements,
        match: [{ path: PATHS.careerAchievements }],
        icon: ACCENT_ICON.achievements,
      },
    ],
  },
];

/** Заголовки групп с несколькими пунктами. */
const GROUP_TITLE: Record<string, string> = {
  skills: 'careerCenter.nav.skills',
  work: 'careerCenter.nav.work',
};

const ITEM_LABEL: Record<string, string> = {
  plan: 'careerCenter.nav.plan',
  learning: 'careerCenter.nav.learning',
  skillsMap: 'careerCenter.nav.skillsMap',
  professions: 'careerCenter.nav.professions',
  jobs: 'careerCenter.nav.jobs',
  applications: 'careerCenter.nav.applications',
  profile: 'careerCenter.nav.profile',
  achievements: 'careerCenter.nav.achievements',
};

export const isItemActive = (item: Pick<IItem, 'match'>, pathname: string): boolean =>
  item.match.some(({ path, exact }) =>
    exact ? pathname === path || pathname === `${path}/` : pathname.startsWith(path),
  );

interface ICareerSectionNav {
  badges?: ICareerNavBadges;
}

/**
 * Фокус после выбора пункта в drawer — на заголовок новой страницы.
 *
 * 🔴 `setTimeout`, а не сразу: rc-drawer зовёт `afterOpenChange` ДО того, как
 * вернёт фокус на кнопку открытия, и немедленный фокус он бы перебил.
 */
const focusPageHeading = () =>
  window.setTimeout(() => {
    const target =
      document.querySelector<HTMLElement>('[data-career-content] h1') ??
      document.querySelector<HTMLElement>('[data-career-content]');
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus();
  }, 0);

export const CareerSectionNav = memo(({ badges = {} }: ICareerSectionNav) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const navigated = useRef(false);

  const list = (onNavigate?: () => void) => (
    <NavList badges={badges} pathname={pathname} onNavigate={onNavigate} />
  );

  return (
    <>
      <DesktopNav aria-label={t('careerCenter.nav.label')}>{list()}</DesktopNav>

      <MobileBar>
        <MenuButton
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}>
          <CareerIcon name="menu" size={20} />
          <span>{t('careerCenter.nav.menu')}</span>
        </MenuButton>
        <Drawer
          open={open}
          placement="left"
          width="min(320px, 85vw)"
          title={t('careerCenter.nav.menu')}
          // 0073: свой крестик — у antd он подписан «Close» при любой локали.
          closable={false}
          extra={<CareerCloseButton onClick={() => setOpen(false)} />}
          onClose={() => setOpen(false)}
          afterOpenChange={(visible) => {
            if (!visible && navigated.current) {
              navigated.current = false;
              focusPageHeading();
            }
          }}>
          <DrawerNav aria-label={t('careerCenter.nav.label')}>
            {list(() => {
              navigated.current = true;
              setOpen(false);
            })}
          </DrawerNav>
        </Drawer>
      </MobileBar>
    </>
  );
});

interface INavList {
  badges: ICareerNavBadges;
  pathname: string;
  onNavigate?: () => void;
}

const NavList = ({ badges, pathname, onNavigate }: INavList) => {
  const { t } = useTranslation();
  const locked = new Set(badges.locked ?? []);

  const badgeFor = (item: IItem): string | null => {
    if (item.key === 'learning' && badges.lessons) return badges.lessons;
    if (item.key === 'applications' && badges.applications) return String(badges.applications);
    return null;
  };

  return (
    <Groups>
      {NAV_GROUPS.map((group) => {
        const title = GROUP_TITLE[group.key];
        return (
          <li key={group.key}>
            {title ? <GroupTitle>{t(title)}</GroupTitle> : null}
            <Items>
              {group.items.map((item) => {
                const active = isItemActive(item, pathname);
                const isLocked = Boolean(item.capability && locked.has(item.capability));
                const badge = isLocked ? null : badgeFor(item);
                return (
                  <li key={item.key}>
                    <Item
                      to={item.to}
                      aria-current={active ? 'page' : undefined}
                      onClick={onNavigate}
                      $active={active}>
                      <ItemIcon aria-hidden>
                        <CareerIcon name={item.icon} size={20} />
                      </ItemIcon>
                      <Label>{t(ITEM_LABEL[item.key])}</Label>
                      {isLocked ? (
                        <Trailing title={t('careerCenter.nav.locked')}>
                          <CareerIcon name="lock" size={16} />
                        </Trailing>
                      ) : badge ? (
                        <Count>{badge}</Count>
                      ) : null}
                    </Item>
                  </li>
                );
              })}
            </Items>
          </li>
        );
      })}
    </Groups>
  );
};

const DesktopNav = styled.nav`
  position: sticky;
  top: 24px;
  align-self: start;
  max-height: calc(100vh - 48px);
  overflow-y: auto;

  ${mobile} {
    display: none;
  }
`;

const MobileBar = styled.div`
  display: none;

  ${mobile} {
    display: block;
  }
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 16px;
  border: 1px solid ${careerColor.line};
  border-radius: ${careerLayout.controlRadius}px;
  background: ${careerColor.bg};
  color: ${careerColor.text};
  cursor: pointer;
  ${careerType.bodyStrong()};
`;

const DrawerNav = styled.nav`
  ${careerTokenVars}
`;

const Groups = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GroupTitle = styled.p`
  margin: 24px 0 8px;
  padding: 0 12px;
  color: ${careerColor.text2};
  ${careerType.groupTitle()};
`;

const Items = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemIcon = styled.span`
  flex: none;
  display: inline-flex;
  color: ${careerColor.text2};
`;

const Label = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Count = styled.span`
  flex: none;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const Trailing = styled.span`
  flex: none;
  display: inline-flex;
  color: ${careerColor.text2};
`;

const Item = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 12px;
  border-radius: ${careerLayout.controlRadius}px;
  color: ${careerColor.text};
  text-decoration: none;
  ${careerType.body()};

  &:hover {
    color: ${careerColor.text};
    background: ${careerColor.hover};
  }

  ${({ $active }) =>
    $active &&
    css`
      background: ${careerColor.active};
      ${careerType.bodyStrong()};

      &:hover {
        background: ${careerColor.active};
      }

      ${ItemIcon} {
        color: ${careerColor.text};
      }
    `}
`;
